import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChecklistIcon from '@mui/icons-material/Checklist';
import CodeIcon from '@mui/icons-material/Code';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import LinkIcon from '@mui/icons-material/Link';
import TextFieldsIcon from '@mui/icons-material/TextFields';
const GROUPS = {
    formatting: 'Formatting',
    lists: 'Lists',
    insert: 'Insert',
};
const hasExtension = (editor, name) => {
    return editor.extensionManager.extensions.some((extension) => extension.name === name);
};
const createParagraphItem = () => ({
    id: 'paragraph',
    title: 'Text',
    description: 'Start writing with plain paragraph text.',
    icon: TextFieldsIcon,
    keywords: ['paragraph', 'normal', 'text'],
    group: GROUPS.formatting,
    run: ({ editor }) => editor.chain().focus().setParagraph().run(),
});
const createHeadingItem = (level) => {
    const labelByLevel = {
        1: 'H1',
        2: 'H2',
        3: 'H3',
    };
    return {
        id: `heading-${level}`,
        title: `Heading ${level}`,
        description: 'Format text as a section heading.',
        icon: undefined,
        prefixLabel: labelByLevel[level],
        keywords: ['title', `h${level}`, 'heading'],
        group: GROUPS.formatting,
        isAvailable: (editor) => hasExtension(editor, 'heading'),
        run: ({ editor }) => editor.chain().focus().toggleHeading({ level }).run(),
    };
};
const createBulletListItem = () => ({
    id: 'bullet-list',
    title: 'Bulleted list',
    description: 'Create a simple bulleted list.',
    icon: FormatListBulletedIcon,
    keywords: ['unordered', 'list', 'bullet'],
    group: GROUPS.lists,
    isAvailable: (editor) => hasExtension(editor, 'bulletList'),
    run: ({ editor }) => editor.chain().focus().toggleBulletList().run(),
});
const createOrderedListItem = () => ({
    id: 'ordered-list',
    title: 'Numbered list',
    description: 'Create a list with numbering.',
    icon: FormatListNumberedIcon,
    keywords: ['ordered', 'numbers', 'list'],
    group: GROUPS.lists,
    isAvailable: (editor) => hasExtension(editor, 'orderedList'),
    run: ({ editor }) => editor.chain().focus().toggleOrderedList().run(),
});
const createTaskListItem = () => ({
    id: 'task-list',
    title: 'Checklist',
    description: 'Track to-dos with checkboxes.',
    icon: ChecklistIcon,
    keywords: ['tasks', 'todos', 'list'],
    group: GROUPS.lists,
    isAvailable: (editor) => hasExtension(editor, 'taskList'),
    run: ({ editor }) => editor.chain().focus().toggleTaskList().run(),
});
const createBlockquoteItem = () => ({
    id: 'blockquote',
    title: 'Quote',
    description: 'Call out a quote or key takeaway.',
    icon: FormatQuoteIcon,
    keywords: ['quote', 'blockquote', 'callout'],
    group: GROUPS.insert,
    isAvailable: (editor) => hasExtension(editor, 'blockquote'),
    run: ({ editor }) => editor.chain().focus().toggleBlockquote().run(),
});
const createCodeBlockItem = () => ({
    id: 'code-block',
    title: 'Code block',
    description: 'Capture code snippets with monospaced formatting.',
    icon: CodeIcon,
    keywords: ['code', 'snippet'],
    group: GROUPS.insert,
    isAvailable: (editor) => hasExtension(editor, 'codeBlock'),
    run: ({ editor }) => editor.chain().focus().toggleCodeBlock().run(),
});
const createDividerItem = () => ({
    id: 'divider',
    title: 'Divider',
    description: 'Separate sections with a divider line.',
    icon: HorizontalRuleIcon,
    keywords: ['divider', 'line', 'separator'],
    group: GROUPS.insert,
    isAvailable: (editor) => hasExtension(editor, 'horizontalRule'),
    run: ({ editor }) => editor.chain().focus().setHorizontalRule().run(),
});
// commented out for now, maybe we can add it later
// const createMentionItem = (): SlashCommandItem => ({
//   id: 'mention',
//   title: 'Mention',
//   description: 'Mention a teammate or entity.',
//   icon: AlternateEmailIcon,
//   keywords: ['mention', 'tag', 'person'],
//   group: GROUPS.insert,
//   isAvailable: (editor) => hasExtension(editor, 'mention'),
//   run: ({ editor, range }) =>
//     editor
//       .chain()
//       .focus()
//       .insertContentAt(range, [
//         { type: 'text', text: '@' },
//         { type: 'text', text: '' },
//       ])
//       .run(),
// });
const createAttachmentItem = () => ({
    id: 'attachment',
    title: 'Attach files',
    description: 'Upload files or images.',
    icon: AttachFileIcon,
    keywords: ['upload', 'file', 'image', 'media'],
    group: GROUPS.insert,
    isAvailable: (_editor, helpers) => Boolean(helpers?.openFilePicker),
    run: ({ helpers }) => {
        if (helpers.openFilePicker) {
            helpers.openFilePicker();
            return true;
        }
        return false;
    },
});
const createLinkItem = () => ({
    id: 'link',
    title: 'Add link',
    description: 'Insert a hyperlink.',
    icon: LinkIcon,
    keywords: ['link', 'url', 'hyperlink'],
    group: GROUPS.insert,
    isAvailable: (_editor, helpers) => Boolean(helpers?.openLinkPopover),
    run: ({ editor, helpers }) => {
        if (!helpers.openLinkPopover) {
            return false;
        }
        const { from, to } = editor.state.selection;
        const selectedText = editor.state.doc.textBetween(from, to, ' ');
        helpers.openLinkPopover(selectedText ?? '');
        return true;
    },
});
/**
 * Builds the default list of slash command items supported by the editor.
 */
export const buildDefaultSlashCommandItems = (editor, helpers) => {
    const items = [
        createParagraphItem(),
        createHeadingItem(1),
        createHeadingItem(2),
        createHeadingItem(3),
        createBulletListItem(),
        createOrderedListItem(),
        createTaskListItem(),
        createBlockquoteItem(),
        createCodeBlockItem(),
        createDividerItem(),
        // future feature
        // createMentionItem(),
        createAttachmentItem(),
        createLinkItem(),
    ];
    return items.filter((item) => !item.isAvailable || item.isAvailable(editor, helpers));
};
